import React, { useMemo } from 'react';
import { useGetSpaceQuery, useListPagesQuery, usePagesPaths } from '../../store';
import { useSpaceAndPageIDFromRouter } from '../../data/space/hooks';
import PageLinkContext from '../../sharedComponents/TextEditor/context/PageLinkContextProvider/context';
import { BLOCK_ELEMENT_KEY, BLOCK_TYPE } from '../../sharedComponents/TextEditor/types';
import { DreamBlock } from '../../sharedComponents/TextEditor/plate';

function PageLinkProviderBase({
  children,
}: {children: React.ReactNode}) {
  const [spaceID] = useSpaceAndPageIDFromRouter();
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { data: pages } = useListPagesQuery([spaceID]);
  const routes = space?.routes;
  const spaceSlug = space?.slug;
  const pagesPaths = usePagesPaths(spaceID);

  const getURLRaw = (
    elementURL: string,
    internal: boolean,
    pageID: string,
    collectionInternal: boolean,
    collectionLinkDataID: string,
    collectionLinkSlug: string,
  ): string => {
    const linkedPage = internal
      && pageID
      && pagesPaths?.[pageID]
      ? pagesPaths?.[pageID]
      : null;

    // elementURL should have been filled with the page path
    // at the time of entering the link as a backup
    let linkedPageLink = linkedPage && linkedPage.path ? `/${linkedPage.path.join('/')}` : elementURL;

    if (internal && collectionInternal) {
      linkedPageLink = linkedPage && linkedPage.path ? `/${linkedPage.path.join('/')}/${collectionLinkSlug}` : elementURL;
    }

    return linkedPageLink;
  };

  const getURL = (element: DreamBlock): string => {
    if (!element) return '';
    const elementURL = element.type === BLOCK_TYPE.IMAGE
      ? element[BLOCK_ELEMENT_KEY.HREF]
      : element[BLOCK_ELEMENT_KEY.URL];

    const internal = element[BLOCK_ELEMENT_KEY.INTERNAL_LINK];
    const pageID = element[BLOCK_ELEMENT_KEY.PAGE_ID];

    return getURLRaw(
      elementURL,
      internal,
      pageID,
      element[BLOCK_ELEMENT_KEY.COLLECTION_INTERNAL_LINK],
      element[BLOCK_ELEMENT_KEY.COLLECTION_LINK_DATA_ID],
      element[BLOCK_ELEMENT_KEY.COLLECTION_LINK_SLUG],
    );
  };

  const getEditorURL = (element: DreamBlock): string => {
    if (!element) return '';
    const elementURL = element.type === BLOCK_TYPE.IMAGE
      ? element[BLOCK_ELEMENT_KEY.HREF]
      : element[BLOCK_ELEMENT_KEY.URL];

    const internal = element[BLOCK_ELEMENT_KEY.INTERNAL_LINK];
    const pageID = element[BLOCK_ELEMENT_KEY.PAGE_ID];

    // Check if internal page pageID exists
    let linkedPageLink = internal && pagesPaths?.[pageID] ? `/s/${spaceSlug}/${spaceID}/${pageID}` : elementURL;

    // IF COLLECTION INTERNAL LINK
    if (internal && element[BLOCK_ELEMENT_KEY.COLLECTION_INTERNAL_LINK]) {
      const dataID = element[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
      linkedPageLink = pagesPaths?.[pageID] ? `/col/${spaceSlug}/${spaceID}/${pageID}/${dataID}` : elementURL;
    }
    return linkedPageLink;
  };

  return (
    <PageLinkContext.Provider
      value={useMemo(() => ({
        pagesPaths,
        getURL,
        getURLRaw,
        getEditorURL,
      }), [routes, pages])}
    >
      {children}
    </PageLinkContext.Provider>
  );
}

const PageLinkEditorProvider = React.memo(PageLinkProviderBase);
export default PageLinkEditorProvider;
